/** @prettier */

/**
 * RT通信一覧（熱源機の台数）
 */

import {Card, CardContent, Stack, Box, Grid} from '@mui/material';
import {useLocation, Link, useParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import CardContents from '../../component/CardContents';
//API import
import {getCompositionsRTUnit} from '../../API/API_Collection';
//Style import
import '../../css/common/common.css';

import {limit} from '../../define';

const RTSetting = () => {
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {siteID, gwID} = useParams();

  const [qton1Num, setQton1Num] = useState(0); //1系統Q-tonの台数
  const [qton2Num, setQton2Num] = useState(0); //2系統Q-tonの台数
  const [chiller1Num, setChiller1Num] = useState(0); //1系統Chillerの台数
  const [chiller2Num, setChiller2Num] = useState(0); //2系統Chillerの台数
  const [hyozanOU1Num, setHyozanOU1Num] = useState(0); //1系統Hyozan室外機の台数
  const [hyozanOU2Num, setHyozanOU2Num] = useState(0); //2系統Hyozan室外機の台数
  const [hyozanIU1Num, setHyozanIU1Num] = useState(0); //1系統Hyozan室内機の台数
  const [hyozanIU2Num, setHyozanIU2Num] = useState(0); //2系統Hyozan室内機の台数

  const trans = useTranslation().t; //翻訳関数

  useEffect(() => {
    if (siteID === undefined || gwID === undefined) return;

    getCompositionsRTUnit(siteID, gwID, {
      'fields': 'system,unit-type,register-number',
      'max-result': limit.REGISTRATION_NUM_RT_UNIT_MAX * 2,
    }).then(response => {
      let qton1NumTemp = 0;
      let qton2NumTemp = 0;
      let chiller1NumTemp = 0;
      let chiller2NumTemp = 0;
      let hyozanOU1NumTemp = 0;
      let hyozanOU2NumTemp = 0;
      let hyozanIU1NumTemp = 0;
      let hyozanIU2NumTemp = 0;
      response['unit-informations'].forEach(unitInfo => {
        switch (unitInfo['unit-type']) {
          case 'QtonClose': //fall through
          case 'QtonOpen':
            if (unitInfo.system === '1') {
              qton1NumTemp++;
            } else if (unitInfo.system === '2') {
              qton2NumTemp++;
            }
            break;
          case 'Chiller':
            if (unitInfo.system === '1') {
              chiller1NumTemp++;
            } else if (unitInfo.system === '2') {
              chiller2NumTemp++;
            }
            break;
          case 'Hyozan':
            if (unitInfo.system === '1') {
              hyozanOU1NumTemp++;
              if (unitInfo['register-number'] !== undefined) {
                hyozanIU1NumTemp += Number(unitInfo['register-number']);
              }
            } else if (unitInfo.system === '2') {
              hyozanOU2NumTemp++;
              if (unitInfo['register-number'] !== undefined) {
                hyozanIU2NumTemp += Number(unitInfo['register-number']);
              }
            }
            break;
        }
      });
      setQton1Num(qton1NumTemp);
      setQton2Num(qton2NumTemp);
      setChiller1Num(chiller1NumTemp);
      setChiller2Num(chiller2NumTemp);
      setHyozanOU1Num(hyozanOU1NumTemp);
      setHyozanOU2Num(hyozanOU2NumTemp);
      setHyozanIU1Num(hyozanIU1NumTemp);
      setHyozanIU2Num(hyozanIU2NumTemp);
    });
  }, [siteID, gwID]);

  /**
   * Hyozanの台数
   * @param props props
   * - ouNum 室外機の台数
   * - iuNum 室内機の台数
   * @returns JSX
   */
  const HyozanNum = (props: {ouNum: number; iuNum: number}) => {
    return (
      <Grid container>
        <Grid item xs={5}>
          {trans('RTSetting.item.value.units.outdoorUnit')}
        </Grid>
        <Grid item xs={1}>
          :
        </Grid>
        <Grid item xs={6}>
          {props.ouNum}&nbsp;{trans('RTSetting.item.value.units.unit')}
        </Grid>
        <Grid item xs={5}>
          {trans('RTSetting.item.value.units.indoorUnit')}
        </Grid>
        <Grid item xs={1}>
          :
        </Grid>
        <Grid item xs={6}>
          {props.iuNum}&nbsp;{trans('RTSetting.item.value.units.unit')}
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {/* １系統 */}
      <Card className="gwSetting__contents--communication">
        <CardContent>
          <Stack spacing={4}>
            <Box className="gwSetting__contents__title">
              <Link
                to={`${location.pathname}/rt1/rt-registration${location.search}`}
                id="mv-linkRtSetting1">
                {trans('RTSetting.linkTxt.remoteTerminal1')}
              </Link>
            </Box>
            <Stack spacing={2} className="gwSetting__contents__contents">
              {/* Q-ton台数 */}
              {qton1Num > 0 ? (
                <CardContents
                  class="gwSetting__contents__contents__underLine"
                  valueDescription={trans('RTSetting.item.title.qton')}
                  value={`${qton1Num} ${trans(
                    'RTSetting.item.value.units.unit'
                  )}`}
                />
              ) : (
                <></>
              )}
              {/* Chiller 台数 */}
              {chiller1Num > 0 ? (
                <CardContents
                  class="gwSetting__contents__contents__underLine"
                  valueDescription={trans('RTSetting.item.title.chiller')}
                  value={`${chiller1Num} ${trans(
                    'RTSetting.item.value.units.unit'
                  )}`}
                />
              ) : (
                <></>
              )}
              {/* Hyozan台数 */}
              {hyozanOU1Num > 0 ? (
                <CardContents
                  class="gwSetting__contents__contents__underLine"
                  valueDescription={trans('RTSetting.item.title.hyozan')}
                  value={
                    <HyozanNum ouNum={hyozanOU1Num} iuNum={hyozanIU1Num} />
                  }
                />
              ) : (
                <></>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      {/* ２系統 */}
      <Card className="gwSetting__contents--communication">
        <CardContent>
          <Stack spacing={4}>
            <Box className="gwSetting__contents__title">
              <Link
                to={`${location.pathname}/rt2/rt-registration${location.search}`}
                id="mv-linkRtSetting2">
                {trans('RTSetting.linkTxt.remoteTerminal2')}
              </Link>
            </Box>
            <Stack spacing={2} className="gwSetting__contents__contents">
              {/* Q-ton台数 */}
              {qton2Num > 0 ? (
                <CardContents
                  class="gwSetting__contents__contents__underLine"
                  valueDescription={trans('RTSetting.item.title.qton')}
                  value={`${qton2Num} ${trans(
                    'RTSetting.item.value.units.unit'
                  )}`}
                />
              ) : (
                <></>
              )}
              {/* Chiller 台数 */}
              {chiller2Num > 0 ? (
                <CardContents
                  class="gwSetting__contents__contents__underLine"
                  valueDescription={trans('RTSetting.item.title.chiller')}
                  value={`${chiller2Num} ${trans(
                    'RTSetting.item.value.units.unit'
                  )}`}
                />
              ) : (
                <></>
              )}
              {/* Hyozan台数 */}
              {hyozanOU2Num > 0 ? (
                <CardContents
                  class="gwSetting__contents__contents__underLine"
                  valueDescription={trans('RTSetting.item.title.hyozan')}
                  value={
                    <HyozanNum ouNum={hyozanOU2Num} iuNum={hyozanIU2Num} />
                  }
                />
              ) : (
                <></>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
};
export default RTSetting;
